import { useEffect, useState } from "react";

/**
 * Chrome/Android 계열이 설치 가능 조건을 만족하면 발생시키는 이벤트.
 * 표준 타입에 아직 없어 직접 정의한다.
 */
export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

declare global {
  interface Window {
    /** index.html 의 인라인 스크립트가 선점해 둔 beforeinstallprompt 이벤트. */
    __installPrompt: BeforeInstallPromptEvent | null;
  }
}

/**
 * 네이티브 설치창 호출 수단.
 *
 * beforeinstallprompt 는 1회성이고 재발행되지 않는다. 브라우저는 manifest·SW 평가
 * 직후 이벤트를 쏘는데 React 마운트가 그보다 늦으면 그대로 유실된다(#175).
 * 그래서 index.html 인라인 스크립트가 선점해 `window.__installPrompt` 에 담아 두고,
 * 여기서는 그것을 회수하는 것이 기본 경로다. 리스너 등록은 마운트가 더 빨랐던
 * 경우를 위한 보조 수단.
 */
export function useInstallPrompt(): {
  /** 네이티브 설치창을 띄울 수 있는 상태인가 */
  canPrompt: boolean;
  /** 설치 완료(appinstalled) 를 감지했는가 */
  installed: boolean;
  promptInstall: () => Promise<void>;
} {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(() =>
    typeof window === "undefined" ? null : (window.__installPrompt ?? null)
  );
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const adopt = (e: BeforeInstallPromptEvent) => setDeferred(e);
    // index.html 이 선점 후 발행하는 신호 (마운트 이후에 이벤트가 온 경우)
    const onSignal = () => {
      if (window.__installPrompt) adopt(window.__installPrompt);
    };
    // 인라인 스크립트가 없는 환경을 위한 직접 수신 경로
    const onPrompt = (e: Event) => {
      e.preventDefault();
      adopt(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      window.__installPrompt = null;
      setDeferred(null);
      setInstalled(true);
    };

    window.addEventListener("safers:installable", onSignal);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("safers:installable", onSignal);
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const promptInstall = async () => {
    if (!deferred) return;
    await deferred.prompt();
    await deferred.userChoice;
    // prompt 는 1회용 — 결과와 무관하게 폐기 (appinstalled 가 후처리)
    window.__installPrompt = null;
    setDeferred(null);
  };

  return { canPrompt: deferred !== null, installed, promptInstall };
}
